/**
 * ceremony_props_loader.ts — the generated ceremony props, loaded once and
 * cloned into stages (the Hugging Face ceremony wave, 2026-09-10).
 *
 * `prefetchCeremonyProps(app)` reads `public/props/ceremony/manifest.json`
 * and parses every listed GLB through AssetModule's loader (KTX2 + meshopt,
 * the tier's texture prep) into a MASTER per key; `ceremonyProp(key)` hands
 * a stage a fitted CLONE — seated by its bounding box bottom-centre and
 * scaled to the catalog height (lib/ceremony_props fitTransform) — or null
 * when the key is not cooked / not landed yet, in which case the stage keeps
 * its procedural piece (the self-heal base, as with the hinterland props).
 * Clones share the master's geometry and materials; they carry
 * `userData.sharedAsset` so `disposeGroup` never frees them.
 */
import * as THREE from 'three/webgpu';
import type { App } from 'enginex/core/App.js';
import { Logger } from 'enginex/core/Logger.js';
import { CEREMONY_PROPS, CEREMONY_PROP_DIR, ceremonyPropUrl, fitTransform, type CeremonyManifest } from '../../lib/ceremony_props.js';

interface Master { object: THREE.Object3D; fit: { scale: number; dx: number; dy: number; dz: number }; sizeM: [number, number, number] }

const masters = new Map<string, Master>();
const stats = { manifest: false, keys: 0, loaded: 0, failed: 0, clones: 0, error: '' };
let started = false;

/** Diag: which props are in (`__ceremonyProps()`). */
export function ceremonyPropStats(): typeof stats & { ready: string[] } {
  return { ...stats, ready: [...masters.keys()] };
}

export async function prefetchCeremonyProps(app: App): Promise<void> {
  if (started) return;
  started = true;
  (window as unknown as Record<string, unknown>).__ceremonyProps = ceremonyPropStats;
  const assets = app.getModule('AssetModule') as { loadUrl?(url: string): Promise<THREE.Object3D> } | undefined;
  if (!assets?.loadUrl) { stats.error = 'no AssetModule'; return; }
  let manifest: CeremonyManifest;
  try {
    const res = await fetch(new URL(`${CEREMONY_PROP_DIR}/manifest.json`, document.baseURI).toString(), { cache: 'no-cache' });
    if (!res.ok) { stats.error = `manifest ${res.status}`; return; }
    manifest = await res.json() as CeremonyManifest;
  } catch (e) { stats.error = `manifest: ${String((e as Error)?.message ?? e)}`; return; }
  stats.manifest = true;
  const entries = Object.entries(manifest.props ?? {}).filter(([key]) => key in CEREMONY_PROPS);
  stats.keys = entries.length;
  for (const [key, ent] of entries) {
    try {
      const obj = await assets.loadUrl(ceremonyPropUrl(ent.file, document.baseURI));
      obj.traverse((o) => { o.userData.sharedAsset = true; });
      const box = new THREE.Box3().setFromObject(obj);
      const fit = fitTransform(box, CEREMONY_PROPS[key].targetH);
      masters.set(key, { object: obj, fit, sizeM: [box.max.x - box.min.x, box.max.y - box.min.y, box.max.z - box.min.z] });
      stats.loaded++;
    } catch (e) {
      stats.failed++;
      Logger.warn('CeremonyProps', `${key}: ${String((e as Error)?.message ?? e)}`);
    }
  }
  Logger.info('CeremonyProps', `${stats.loaded}/${stats.keys} generated ceremony props ready`);
}

/**
 * A fitted clone of a generated prop for a stage, or null (keep the
 * procedural piece). `yaw` turns it about its seat; the group's origin is the
 * seat (bounding box bottom-centre).
 */
export function ceremonyProp(key: string, yaw = 0, longAxis: 'x' | 'z' | null = null): THREE.Group | null {
  const m = masters.get(key);
  if (!m) return null;
  const g = new THREE.Group();
  g.name = `ceremony:${key}`;
  g.userData.sharedAsset = true;
  const clone = m.object.clone(true);
  clone.traverse((o) => { o.userData.sharedAsset = true; });
  clone.scale.setScalar(m.fit.scale);
  clone.position.set(m.fit.dx, m.fit.dy, m.fit.dz);
  // A stage that lays a long prop along one of its axes (the feast tables
  // along x) gets the model's long side there whichever way it was generated:
  // the SEATED clone turns inside a wrapper, so the seat stays the origin.
  const seated = new THREE.Group();
  seated.userData.sharedAsset = true;
  seated.add(clone);
  if (longAxis) {
    const modelLongX = m.sizeM[0] >= m.sizeM[2];
    if ((longAxis === 'x') !== modelLongX) seated.rotation.y = Math.PI / 2;
  }
  g.add(seated);
  g.rotation.y = yaw;
  stats.clones++;
  return g;
}
